<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { sessionHelper } from "@/utils";
import { SHARED } from "@/constants";
import { mapState } from "vuex";
import { CATALOG_MODULE } from "@/state/modules/catalog";
import { productMethods, catalogMethods } from "@/state/helpers";
import { accountingDriver, accountingFamily } from "@/utils/capability.helper";
import { filtersHelper } from "@/utils";

export default {
  page: { title: i18n.t("label.listingX", [i18n.t("module.product")]) },
  components: { Layout, PageHeader },
  data() {
    return {
      title: i18n.t("label.listingX", [i18n.t("module.product")]),
      currentPage: 1,
      filter: "",
      perPage: SHARED.DEFAULT_PAGE_SIZE,
      syncing: false,
      fields: [
        { key: 'itemCode', sortable: true, label: i18n.t('label.itemCode') },
        { key: 'description', sortable: false, label: i18n.t('label.description') },
        { key: 'itemGroup', sortable: true, label: i18n.t('module.product') },
        { key: 'baseUom', sortable: true, label: i18n.t('label.uom') },
        { key: 'source', sortable: false, label: i18n.t('agent.source') },
        { key: 'isActive', sortable: true, label: i18n.t('label.status') },
        { key: 'showInApp', sortable: true, label: i18n.t('label.showInApp') },
        { key: 'action', sortable: false, label: i18n.t('label.action') }
      ],
      searchCriteria: {},
    };
  },
  computed: {
    ...mapState("product", ["status"]),
    ...mapState(CATALOG_MODULE, ["syncStatus", "syncModule"]),
    // The banner and the Sync now button are the catalogue-sync module's. A
    // backend without it has nothing to say about when it last synced.
    hasSyncModule() { return this.syncModule !== false; },
    tableRows() { return this.$store.state.product.list.data; },
    pagination() { return this.$store.state.product.list.pagination; },
    // 'full' (AutoCount / SQL Account) owns items, units and prices; 'lite' (Xero / QuickBooks) leaves units and prices to One Sales.
    isLite() { return accountingFamily() === "lite"; },
    driverLabel() {
      const driver = accountingDriver();
      if (!driver) return i18n.t("accounting.ledger");
      return this.$te(`accounting.driverName.${driver}`) ? i18n.t(`accounting.driverName.${driver}`) : driver;
    },
    lastSyncedLabel() {
      const at = this.syncStatus?.last_synced_at;
      return at ? filtersHelper.getDateTimeFormat(at) : i18n.t("accounting.neverSynced");
    },
    canSyncCatalog() { return this.hasSyncModule && this.$can("Update Configuration"); },
  },
  created() {
    this.searchCriteria = { pageNumber: SHARED.DEFAULT_PAGE_NUMBER, pageSize: SHARED.DEFAULT_PAGE_SIZE, searchArray: [], includeHidden: true };
    this.getList(this.searchCriteria);
    if (this.$can("Read Catalog")) this.getCatalogSyncStatus();
  },
  methods: {
    ...productMethods,
    ...catalogMethods,
    sourceLabel(row) {
      return row.source === "local" ? i18n.t("accounting.oneSales") : i18n.t("accounting.fromX", [this.driverLabel]);
    },
    async syncCatalogNow() {
      if (this.syncing) return;
      this.syncing = true;
      try {
        if (await this.syncCatalog()) {
          this.getList(this.searchCriteria);
          if (this.$can("Read Catalog")) this.getCatalogSyncStatus();
        }
      } finally {
        this.syncing = false;
      }
    },
    getList(filter) {
      this.getAllProducts({ filter });
      this.searchCriteria = filter;
      sessionHelper.storeSearch("PRODUCT_SEARCH", filter);
    },
    pageNavigation(pageNumber) {
      this.searchCriteria.pageNumber = pageNumber;
      this.getList(this.searchCriteria);
    },
    searchListing() {
      this.searchCriteria.pageNumber = SHARED.DEFAULT_PAGE_NUMBER;
      this.searchCriteria.searchArray = [this.filter];
      this.getList(this.searchCriteria);
    },
    goDetail(row) { this.$router.push({ name: "Product-detail", params: { itemCode: row.itemCode } }); }
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="row">
      <div class="col-12">
        <b-alert v-if="hasSyncModule" show :variant="isLite ? 'info' : 'light'" class="border">
          <i class="mdi mdi-information-outline mr-1"></i>
          <span v-if="isLite">{{ $t('catalogSync.ownedByOneSales', [driverLabel, lastSyncedLabel]) }}</span>
          <span v-else>{{ $t('catalogSync.ownedByLedger', [driverLabel, lastSyncedLabel]) }}</span>
        </b-alert>
        <div class="card">
          <div class="card-body">
            <div class="row mt-2 mb-3">
              <div class="col-sm-12 col-md-6">
                <label class="d-inline-flex align-items-center">
                  {{ $t('label.search') }}
                  <b-form-input v-model="filter" type="search" class="form-control form-control-sm ml-2" @change="searchListing"></b-form-input>
                </label>
              </div>
              <div class="col-sm-12 col-md-6 text-right">
                <b-button v-if="canSyncCatalog" variant="outline-primary" size="sm" :disabled="syncing" @click="syncCatalogNow">
                  <b-spinner v-if="syncing" small class="mr-1"></b-spinner>
                  <i v-else class="mdi mdi-sync mr-1"></i>{{ $t('catalogSync.syncNow') }}
                </b-button>
              </div>
            </div>
            <div class="table-responsive mb-0">
              <b-table :items="tableRows" :fields="fields" responsive="sm" :per-page="perPage">
                <template #cell(source)="data">
                  <b-badge :variant="tableRows[data.index].source === 'local' ? 'primary' : 'dark'">{{ sourceLabel(tableRows[data.index]) }}</b-badge>
                </template>
                <template #cell(isActive)="data">
                  <b-badge variant="success" v-if="tableRows[data.index].isActive">{{ $t('label.active') }}</b-badge>
                  <b-badge variant="secondary" v-else>{{ $t('label.inactive') }}</b-badge>
                </template>
                <template #cell(showInApp)="data">
                  <b-badge variant="success" v-if="tableRows[data.index].showInApp">{{ $t('label.showInApp') }}</b-badge>
                  <b-badge variant="dark" v-else>{{ $t('label.hiddenInApp') }}</b-badge>
                </template>
                <template #cell(isConsignmentSetItem)="data">
                  <b-badge variant="primary" v-if="tableRows[data.index].isConsignmentSetItem">{{ $t('label.starterSet') }} / {{ $t('label.exchangeSet') }}</b-badge>
                  <span v-else class="text-muted">—</span>
                </template>
                <template #cell(action)="data">
                  <a href="javascript:void(0);" @click="goDetail(tableRows[data.index])" class="text-info" v-b-tooltip.hover :title="$t('label.view')">
                    <i class="mdi mdi-eye-outline font-size-18"></i>
                  </a>
                </template>
              </b-table>
            </div>
            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination v-model="currentPage" :total-rows="pagination.total" :per-page="perPage" @change="pageNavigation"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
